/**
 * Battle action log: server-authoritative. Opens a full drawer from a single button.
 */
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BattleActionLogDrawer from './BattleActionLogDrawer';
import { ThumbnailWithLabel } from './BattleActionLogRow';
import { collectRecentBattleActionLogThumbnails } from './battleActionLogRecentThumbnails';
import './BattleActionLog.css';

const HEADER_PREVIEW_THUMB_LIMIT = 3;

function BattleActionLog({
  actionLog = [],
  gameState,
  userId,
  opponentId,
  currentUsername = '',
  opponentUsername = '',
  isMobile = false,
  inlineInUserRow = false,
  fixedDesktopExpandedPosition = null,
  orderDesignScale = 1,
  unitRegistryRef = null
}) {
  const { t } = useTranslation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const headerPreviewThumbs = useMemo(
    () => collectRecentBattleActionLogThumbnails(actionLog, userId, HEADER_PREVIEW_THUMB_LIMIT),
    [actionLog, userId]
  );

  return (
    <>
      <div
        className={[
          'battle-action-log-container',
          isMobile ? 'battle-action-log-container--mobile' : '',
          inlineInUserRow ? 'battle-action-log-container--inline-user-row' : '',
        ].filter(Boolean).join(' ')}
        aria-label={t('battleUI.actionHistory.title')}
      >
        <button
          type="button"
          className={[
            'battle-action-log-button',
            isMobile ? 'battle-action-log-button--mobile' : '',
          ].filter(Boolean).join(' ')}
          onClick={() => setDrawerOpen(true)}
          aria-expanded={drawerOpen}
          aria-haspopup="dialog"
        >
          <span
            className={[
              'battle-action-log-button-label',
              isMobile && headerPreviewThumbs.length > 0 ? 'battle-action-log-button-label--mobile-hidden' : '',
            ].filter(Boolean).join(' ')}
          >
            {isMobile && headerPreviewThumbs.length === 0
              ? t('battleUI.actionHistory.drawerOpenButtonShort')
              : t('battleUI.actionHistory.drawerOpenButton')}
          </span>
          {headerPreviewThumbs.length > 0 ? (
            <div className="battle-action-log-header-thumbs" aria-hidden>
              {headerPreviewThumbs.map(({ descriptor, entryKey }) => (
                <div key={entryKey} className="battle-action-log-header-thumb battle-action-log-header-thumb--order">
                  <ThumbnailWithLabel
                    orderLike={descriptor.orderLike}
                    gameState={gameState}
                    unitRegistryRef={unitRegistryRef}
                    zoneName="tooltip"
                    showSkull={descriptor.showSkull}
                    healthDelta={descriptor.healthDelta}
                    intelDelta={descriptor.intelDelta}
                    generalDelta={descriptor.generalDelta}
                    compactHeaderPreview={isMobile}
                  />
                </div>
              ))}
            </div>
          ) : null}
        </button>
      </div>
      <BattleActionLogDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        actionLog={actionLog}
        gameState={gameState}
        userId={userId}
        opponentId={opponentId}
        currentUsername={currentUsername}
        opponentUsername={opponentUsername}
        isMobile={isMobile}
        fixedDesktopExpandedPosition={fixedDesktopExpandedPosition}
        orderDesignScale={orderDesignScale}
        unitRegistryRef={unitRegistryRef}
      />
    </>
  );
}

export default BattleActionLog;
